import { useMemo } from 'react';
import { noop } from 'lodash';

import { useTranslation } from '@lyearn/i18n';
import { UseOptionsQueryParams, UseOptionsQueryReturnType } from '@/components/FilterNew/types';
import useJourneyData from '@/modules/journey/views/JourneyReport/hooks/useJourneyData';

export interface UseBatchOptionsQueryParams {
  journeyId?: string;
}

const useBatchOptionsQuery = ({
  searchText,
  journeyId = '',
}: UseBatchOptionsQueryParams & UseOptionsQueryParams): UseOptionsQueryReturnType => {
  const { t } = useTranslation();

  const getBatchFilterOption = (batchConfig: any): any => ({
    value: batchConfig.batchId,
    label: batchConfig.name || '',
  });

  const { journey, fetching, error } = useJourneyData({ journeyId });

  const batchConfig = useMemo(() => {
    return (
      journey.participantConfigs.batchConfigs
        ?.filter((config) => !!config.canManageBatch)
        ?.map((batchConfig) => ({
          name: batchConfig.name ?? '',
          batchId: batchConfig.groupId,
        })) ?? []
    );
  }, [journey]);

  const filteredBatchConfig = useMemo(() => {
    if (!searchText) {
      return batchConfig;
    }

    return batchConfig.filter((config) =>
      config.name.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [batchConfig, searchText]);

  const groups = useMemo(() => {
    if (!filteredBatchConfig?.length) {
      return [];
    }

    const adaptedUsers = filteredBatchConfig.map((config) => getBatchFilterOption(config));

    return [
      {
        id: 'batchId',
        options: adaptedUsers,
      },
    ];
  }, [filteredBatchConfig]);

  return {
    error: undefined,
    groups,
    fetching,
    fetchMore: noop,
    renderFooter: undefined,
  };
};

export default useBatchOptionsQuery;
